import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import * as XLSX from 'xlsx';

import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CloseIcon from '@mui/icons-material/Close';

import { fetchStockPerformanceReport } from '../../../api';

const TAB_KEYS = [
  { key: 'TODAY', label: 'Today' },
  { key: 'LAST_7', label: '7 Days' },
  { key: 'LAST_30', label: '30 Days' },
  { key: 'THIS_MONTH', label: 'This Month' },
];

const getDateString = (date) => date.toISOString().slice(0, 10);

const getDateRangeFromTab = (tabKey) => {
  const today = new Date();
  const to = getDateString(today);

  if (tabKey === 'TODAY') {
    return { from: to, to };
  }

  if (tabKey === 'LAST_7') {
    const fromDate = new Date();
    fromDate.setDate(today.getDate() - 6);
    return { from: getDateString(fromDate), to };
  }

  if (tabKey === 'LAST_30') {
    const fromDate = new Date();
    fromDate.setDate(today.getDate() - 29);
    return { from: getDateString(fromDate), to };
  }

  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  return { from: getDateString(monthStart), to };
};

const formatMoney = (value) => {
  return `Rs. ${Number(value || 0).toLocaleString('en-LK', {
    maximumFractionDigits: 2,
  })}`;
};

const formatNumber = (value) => Number(value || 0).toLocaleString('en-LK');

const formatExcelDateOnly = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-LK');
};

const setColumnWidths = (worksheet, widths) => {
  worksheet['!cols'] = widths.map((width) => ({ wch: width }));
};

const getItemName = (item) => {
  return (
    item.itemName ||
    item.medicineName ||
    item.brandName ||
    item.genericName ||
    'Unknown item'
  );
};

const buildFastMovingRows = (reportData) => {
  return (reportData?.fastMovingItems || []).map((item) => ({
    Item: getItemName(item),
    Batch: item.batchNo || '',
    'Qty Sold': item.qtySold || 0,
    Bills: item.invoiceCount || 0,
    Revenue: item.revenue || 0,
    'Current Stock': item.currentStock || 0,
    'Stock Status': item.stockStatus || '',
    'Expiry Date': formatExcelDateOnly(item.expiryDate),
    'Expiry Status': item.expiryStatus || '',
  }));
};

const buildSlowMovingRows = (reportData) => {
  return (reportData?.slowMovingItems || []).map((item) => ({
    Item: getItemName(item),
    Batch: item.batchNo || '',
    'Current Stock': item.currentStock || 0,
    'Qty Sold': item.qtySold || 0,
    'Stock Value': item.stockValue || 0,
    'Last Sold Date': formatExcelDateOnly(item.lastSoldDate),
    'Days Since Last Sale':
      item.daysSinceLastSale == null ? '' : item.daysSinceLastSale,
    'Expiry Date': formatExcelDateOnly(item.expiryDate),
    'Expiry Status': item.expiryStatus || '',
    'Stock Status': item.stockStatus || '',
    'Movement Status': item.movementStatus || '',
  }));
};

const downloadStockPerformanceExcel = ({ filename, reportData }) => {
  if (!reportData) return;

  const fastRows = buildFastMovingRows(reportData);
  const slowRows = buildSlowMovingRows(reportData);

  const fastWorksheet = XLSX.utils.json_to_sheet(fastRows);
  const slowWorksheet = XLSX.utils.json_to_sheet(slowRows);

  setColumnWidths(fastWorksheet, [
    34, // Item
    16, // Batch
    12, // Qty Sold
    10, // Bills
    16, // Revenue
    16, // Current Stock
    16, // Stock Status
    16, // Expiry Date
    18, // Expiry Status
  ]);

  setColumnWidths(slowWorksheet, [
    34, // Item
    16, // Batch
    16, // Current Stock
    12, // Qty Sold
    16, // Stock Value
    18, // Last Sold Date
    22, // Days Since Last Sale
    16, // Expiry Date
    18, // Expiry Status
    16, // Stock Status
    20, // Movement Status
  ]);

  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, fastWorksheet, 'Fast Moving Items');
  XLSX.utils.book_append_sheet(workbook, slowWorksheet, 'Slow Moving Items');

  XLSX.writeFile(workbook, filename);
};

const StockPerformanceReportCard = () => {
  const defaultRange = getDateRangeFromTab('THIS_MONTH');

  const [tab, setTab] = useState('THIS_MONTH');
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);

  const [reportData, setReportData] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadReport = async (params = { from, to }) => {
    try {
      setLoading(true);
      setError('');

      const res = await fetchStockPerformanceReport(params);
      setReportData(res.data);
    } catch (err) {
      console.error('Failed to fetch stock performance report:', err);
      setError(
        err?.response?.data?.message ||
          'Failed to load stock performance report.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport({ from, to });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (_e, value) => {
    setTab(value);

    const range = getDateRangeFromTab(value);
    setFrom(range.from);
    setTo(range.to);

    loadReport(range);
  };

  const handleFromChange = (e) => {
    const value = e.target.value;
    setFrom(value);
    setTab(false);
    loadReport({ from: value, to });
  };

  const handleToChange = (e) => {
    const value = e.target.value;
    setTo(value);
    setTab(false);
    loadReport({ from, to: value });
  };

  const handleDownload = () => {
    downloadStockPerformanceExcel({
      filename: `stock-performance-report-${from}-to-${to}.xlsx`,
      reportData,
    });
  };

  const summary = reportData?.summary || {};
  const fastRows = buildFastMovingRows(reportData);
  const slowRows = buildSlowMovingRows(reportData);

  return (
    <>
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '22px',
          p: { xs: 2, sm: 3 },
          border: '1px solid #e5e7eb',
          boxShadow: '0 10px 28px rgba(15, 23, 42, 0.07)',
          background: '#fff',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
            mb: 3,
          }}
        >
          <Box
            sx={{
              width: 58,
              height: 58,
              borderRadius: '16px',
              background: '#f0fdf4',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <QueryStatsOutlinedIcon
              sx={{
                color: '#16a34a',
                fontSize: 30,
              }}
            />
          </Box>

          <Chip
            label={`${formatNumber((summary.fastMovingItemsCount || 0) + (summary.slowMovingItemsCount || 0))} ${
                (summary.fastMovingItemsCount || 0) + (summary.slowMovingItemsCount || 0) === 1 ? 'item' : 'items'
            }`}
            sx={{
                borderRadius: '999px',
                background: '#dcfce7',
                color: '#166534',
                fontWeight: 800,
                fontSize: 14,
                height: 34,
                px: 1,
            }}
            />
        </Box>

        <Typography
          sx={{
            fontSize: 23,
            fontWeight: 900,
            color: '#111827',
            mb: 1,
          }}
        >
          Stock Performance Report
        </Typography>

        <Typography
          sx={{
            fontSize: 15.5,
            lineHeight: 1.65,
            color: '#6b7280',
            mb: 2.5,
          }}
        >
          Fast-moving and slow-moving medicines, current stock, sales movement and dead stock risk.
        </Typography>

        <Box
          sx={{
            mb: 2.5,
            borderRadius: '20px',
            backgroundColor: '#f3f4f6',
            p: 1.4,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(4, 1fr)',
              },
              gap: 0.8,
            }}
          >
            {TAB_KEYS.map((item) => {
              const active = tab === item.key;

              return (
                <Button
                  key={item.key}
                  onClick={() => handleTabChange(null, item.key)}
                  disableRipple
                  sx={{
                    height: 40,
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: 900,
                    fontSize: { xs: '0.82rem', sm: '0.9rem' },
                    letterSpacing: '0.01em',
                    backgroundColor: active ? '#ffffff' : 'transparent',
                    color: active ? '#111827' : '#6b7280',
                    boxShadow: active
                      ? 'var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), 0 4px 12px rgba(15, 23, 42, 0.10)'
                      : 'none',
                    '&:hover': {
                      backgroundColor: active
                        ? '#ffffff'
                        : 'rgba(255,255,255,0.45)',
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>

          <Box
            sx={{
              height: '1px',
              backgroundColor: '#e5e7eb',
              mt: 1.4,
              mb: 1.4,
            }}
          />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
              },
              gap: 1.4,
            }}
          >
            <DateInput label="From" value={from} onChange={handleFromChange} />
            <DateInput label="To" value={to} onChange={handleToChange} />
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(3, 1fr)',
            },
            gap: 1.2,
            mb: 2.5,
          }}
        >
          <MiniMetric label="Qty Sold" value={formatNumber(summary.totalQtySold)} />
          <MiniMetric label="Fast Items" value={formatNumber(summary.fastMovingItemsCount)} />
          <MiniMetric label="Slow Items" value={formatNumber(summary.slowMovingItemsCount)} />
        </Box>

        <Box sx={{ mt: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '150px 1fr',
            },
            gap: 1.4,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon />}
            onClick={() => setPreviewOpen(true)}
            disabled={loading || !reportData}
            sx={{
              height: 50,
              borderRadius: '12px',
              borderColor: '#e5e7eb',
              color: '#374151',
              fontWeight: 800,
              fontSize: 15,
              textTransform: 'none',
              background: '#fff',
              '&:hover': {
                borderColor: '#cbd5e1',
                background: '#f9fafb',
              },
            }}
          >
            Preview
          </Button>

          <Button
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={18} sx={{ color: '#fff' }} />
              ) : (
                <FileDownloadOutlinedIcon />
              )
            }
            onClick={handleDownload}
            disabled={loading || !reportData}
            sx={{
              height: 50,
              borderRadius: '12px',
              background: '#0f172a',
              color: '#fff',
              fontWeight: 900,
              fontSize: 15,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                background: '#111827',
                boxShadow: 'none',
              },
              '&:disabled': {
                background: '#94a3b8',
                color: '#fff',
              },
            }}
          >
            Download Excel
          </Button>
        </Box>
        </Box>
      </Card>

      <StockPerformancePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        from={from}
        to={to}
        summary={summary}
        fastRows={fastRows}
        slowRows={slowRows}
      />
    </>
  );
};

const DateInput = ({ label, value, onChange }) => {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 800,
          color: '#6b7280',
          mb: 0.7,
          ml: 0.3,
        }}
      >
        {label}
      </Typography>

      <TextField
        type="date"
        size="small"
        value={value}
        onChange={onChange}
        fullWidth
        sx={{
          '& .MuiOutlinedInput-root': {
            height: 52,
            borderRadius: '15px',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
            '& fieldset': {
              border: 'none',
            },
            '&:hover': {
              borderColor: '#d1d5db',
            },
            '&.Mui-focused': {
              borderColor: '#94a3b8',
              boxShadow: '0 0 0 3px rgba(148, 163, 184, 0.18)',
            },
          },
          '& .MuiOutlinedInput-input': {
            fontSize: '1rem',
            fontWeight: 800,
            color: '#333',
            px: 1.6,
          },
        }}
      />
    </Box>
  );
};

const MiniMetric = ({ label, value }) => {
  return (
    <Box
      sx={{
        borderRadius: '14px',
        background: '#f8fafc',
        border: '1px solid #eef2f7',
        p: 1.4,
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          color: '#6b7280',
          fontWeight: 800,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontSize: 15,
          color: '#111827',
          fontWeight: 900,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const StockPerformancePreviewModal = ({
  open,
  onClose,
  from,
  to,
  summary,
  fastRows,
  slowRows,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '22px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 900,
              color: '#111827',
            }}
          >
            Stock Performance Report Preview
          </Typography>

          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: '#6b7280',
              mt: 0.4,
            }}
          >
            {from} to {to}
          </Typography>
        </Box>

        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          backgroundColor: '#f8fafc',
        }}
      >
        <Box sx={{ p: 2.5, backgroundColor: '#ffffff' }}>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 900,
              color: '#111827',
              mb: 1.5,
            }}
          >
            Summary
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 1.4,
            }}
          >
            <PreviewMetric label="Qty Sold" value={formatNumber(summary.totalQtySold)} />
            <PreviewMetric label="Fast Moving Items" value={formatNumber(summary.fastMovingItemsCount)} />
            <PreviewMetric label="Slow Moving Items" value={formatNumber(summary.slowMovingItemsCount)} />
            <PreviewMetric label="Items With No Sales" value={formatNumber(summary.noSalesInRangeCount)} />
            {/* <PreviewMetric label="Dead Stock Items" value={formatNumber(summary.deadStockItemsCount)} /> */}
            <PreviewMetric label="Slow Stock Value" value={formatMoney(summary.slowMovingStockValue)} />
          </Box>
        </Box>

        <Divider />

        <ReportTableSection
          title="Fast Moving Items"
          subtitle="Medicines with the highest quantity sold in the selected date range."
          emptyText="No fast-moving items found for this date range."
          rows={fastRows}
          type="fast"
        />

        <Divider />

        <ReportTableSection
          title="Slow Moving Items"
          subtitle="Medicines with low sales, no sales, or long gaps since the last sale."
          emptyText="No slow-moving items found for this date range."
          rows={slowRows}
          type="slow"
        />
      </DialogContent>
    </Dialog>
  );
};

const ReportTableSection = ({ title, subtitle, emptyText, rows, type }) => {
  const visibleRows = rows || [];

  return (
    <>
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
        }}
      >
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 900,
            color: '#111827',
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            fontSize: 14,
            color: '#6b7280',
            fontWeight: 600,
            mt: 0.4,
          }}
        >
          {subtitle}
        </Typography>
      </Box>

      {visibleRows.length === 0 ? (
        <Box sx={{ p: 4, backgroundColor: '#ffffff' }}>
          <Typography color="text.secondary">{emptyText}</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            p: 2,
            backgroundColor: '#ffffff',
          }}
        >
          <Box
            sx={{
              maxHeight: '45vh',
              overflow: 'auto',
              border: '1px solid #e5e7eb',
              borderRadius: '14px',
              backgroundColor: '#ffffff',
            }}
          >
            {type === 'fast' ? (
              <FastMovingTable rows={visibleRows} />
            ) : (
              <SlowMovingTable rows={visibleRows} />
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

const FastMovingTable = ({ rows }) => {
  return (
    <Table
      stickyHeader
      size="small"
      sx={{
        minWidth: 1050,
        '& th': {
          backgroundColor: '#f3f4f6',
          color: '#374151',
          fontWeight: 900,
          whiteSpace: 'nowrap',
          borderBottom: '1px solid #d1d5db',
        },
        '& td': {
          whiteSpace: 'nowrap',
          borderBottom: '1px solid #eef2f7',
          fontSize: 13,
        },
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell>Item</TableCell>
          <TableCell>Batch</TableCell>
          <TableCell align="right">Qty Sold</TableCell>
          <TableCell align="right">Bills</TableCell>
          <TableCell align="right">Revenue</TableCell>
          <TableCell align="right">Current Stock</TableCell>
          <TableCell>Stock Status</TableCell>
          <TableCell>Expiry Date</TableCell>
          <TableCell>Expiry Status</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {rows.map((row, index) => (
          <TableRow key={`${row.Item}-${row.Batch}-${index}`}>
            <TableCell>{row.Item}</TableCell>
            <TableCell>{row.Batch}</TableCell>
            <TableCell align="right">{formatNumber(row['Qty Sold'])}</TableCell>
            <TableCell align="right">{formatNumber(row.Bills)}</TableCell>
            <TableCell align="right">{formatMoney(row.Revenue)}</TableCell>
            <TableCell align="right">{formatNumber(row['Current Stock'])}</TableCell>
            <TableCell>{row['Stock Status']}</TableCell>
            <TableCell>{row['Expiry Date']}</TableCell>
            <TableCell>{row['Expiry Status']}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const SlowMovingTable = ({ rows }) => {
  return (
    <Table
      stickyHeader
      size="small"
      sx={{
        minWidth: 1250,
        '& th': {
          backgroundColor: '#f3f4f6',
          color: '#374151',
          fontWeight: 900,
          whiteSpace: 'nowrap',
          borderBottom: '1px solid #d1d5db',
        },
        '& td': {
          whiteSpace: 'nowrap',
          borderBottom: '1px solid #eef2f7',
          fontSize: 13,
        },
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell>Item</TableCell>
          <TableCell>Batch</TableCell>
          <TableCell align="right">Current Stock</TableCell>
          <TableCell align="right">Qty Sold</TableCell>
          <TableCell align="right">Stock Value</TableCell>
          <TableCell>Last Sold Date</TableCell>
          <TableCell align="right">Days Since Last Sale</TableCell>
          <TableCell>Expiry Date</TableCell>
          <TableCell>Expiry Status</TableCell>
          <TableCell>Stock Status</TableCell>
          <TableCell>Movement Status</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {rows.map((row, index) => (
          <TableRow key={`${row.Item}-${row.Batch}-${index}`}>
            <TableCell>{row.Item}</TableCell>
            <TableCell>{row.Batch}</TableCell>
            <TableCell align="right">{formatNumber(row['Current Stock'])}</TableCell>
            <TableCell align="right">{formatNumber(row['Qty Sold'])}</TableCell>
            <TableCell align="right">{formatMoney(row['Stock Value'])}</TableCell>
            <TableCell>{row['Last Sold Date']}</TableCell>
            <TableCell align="right">{row['Days Since Last Sale']}</TableCell>
            <TableCell>{row['Expiry Date']}</TableCell>
            <TableCell>{row['Expiry Status']}</TableCell>
            <TableCell>{row['Stock Status']}</TableCell>
            <TableCell>{row['Movement Status']}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const PreviewMetric = ({ label, value }) => {
  return (
    <Box
      sx={{
        backgroundColor: '#f8fafc',
        border: '1px solid #e5e7eb',
        borderRadius: '14px',
        p: 1.5,
      }}
    >
      <Typography
        sx={{
          color: '#6b7280',
          fontSize: 12,
          fontWeight: 800,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          color: '#111827',
          fontSize: 17,
          fontWeight: 900,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

export default StockPerformanceReportCard;